import {
  Box,
  Dialog as ChakraDialog,
  type DialogPositionerProps,
  HStack,
  Portal,
  Separator,
  Stack,
} from '@chakra-ui/react';
import { X } from 'lucide-react';
import * as React from 'react';

import { IconButton } from '../button/icon-button';

export interface ModalContentProps extends Omit<ChakraDialog.ContentProps, 'asChild'> {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
  positionerProps?: DialogPositionerProps;
}

const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(function ModalContent(
  props,
  ref
) {
  const { children, portalled = true, portalRef, backdrop = true, positionerProps, ...rest } = props;

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDialog.Backdrop />}
      <ChakraDialog.Positioner {...positionerProps}>
        {/*
          Overflow stays visible on the dialog surface so Select/DatePicker
          portalRef={contentRef} lists are not cropped. minH={0} lets maxH shrink
          the flex item (overflow:visible restores auto min-size). Header/body/footer
          clip and scroll inside [data-modal-clip] so Save/Cancel stay on screen.
        */}
        <ChakraDialog.Content ref={ref} {...rest} asChild={false} overflow="visible" minH="0">
          <Box
            data-modal-clip=""
            data-testid="modal-clip"
            display="flex"
            flexDirection="column"
            flex="1 1 auto"
            minH="0"
            maxH="100%"
            overflow="hidden"
            w="full"
            borderRadius="inherit"
          >
            {children}
          </Box>
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </Portal>
  );
});

const ModalCloseTrigger = React.forwardRef<HTMLButtonElement, ChakraDialog.CloseTriggerProps>(
  function ModalCloseTrigger(props, ref) {
    const { children, ...rest } = props;

    if (children) {
      return (
        <ChakraDialog.CloseTrigger ref={ref} {...rest} asChild>
          {children}
        </ChakraDialog.CloseTrigger>
      );
    }

    return (
      <ChakraDialog.CloseTrigger {...rest} asChild>
        <IconButton aria-label="Close" icon={X} ref={ref} size="rg" variant="ghost" />
      </ChakraDialog.CloseTrigger>
    );
  }
);

/** KDS footer with inset separator (see `figmaComponentNodes.modal.footer`). */
const ModalFooter = React.forwardRef<HTMLDivElement, ChakraDialog.FooterProps>(function ModalFooter(
  props,
  ref
) {
  const { children, ...rest } = props;

  return (
    <ChakraDialog.Footer ref={ref} {...rest}>
      <Stack gap="4" width="full">
        <Separator alignSelf="stretch" borderColor="neutral.200" />
        <HStack gap="2" justifyContent="flex-end">
          {children}
        </HStack>
      </Stack>
    </ChakraDialog.Footer>
  );
});

const ModalRoot = (props: React.ComponentProps<typeof ChakraDialog.Root>) => (
  <ChakraDialog.Root scrollBehavior="inside" {...props} />
);

export const Modal = {
  Root: ModalRoot,
  Content: ModalContent,
  Header: ChakraDialog.Header,
  Title: ChakraDialog.Title,
  Description: ChakraDialog.Description,
  Body: ChakraDialog.Body,
  Footer: ModalFooter,
  CloseTrigger: ModalCloseTrigger,
  Trigger: ChakraDialog.Trigger,
  ActionTrigger: ChakraDialog.ActionTrigger,
  Backdrop: ChakraDialog.Backdrop,
};
